import React, { useContext, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { adminRegisterProduct } from '../../../api/db/product';
import { registerKind, userType } from '../../../constants/Enum';
import {
  gray10,
  gray100,
  gray70,
  primaryButtonBackgroundColor,
  primaryButtonOutlinedColor
} from '../../../constants/Colors';
import { sideBarWidth, spHeaderHeight } from '../../../constants/Location';
import { adminProductSchema } from '../../../validation/products';
import { AdminProductRegisterType } from '../../../validation/products/productType';

import SideBar from '../../uiParts/SideBar';

import { AxiosError, AxiosResponse } from 'axios';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import CustomLoading from '../../uiParts/CustomLoading';
import { ErrorResponseType, common } from '../../../modules/error';
import { AuthContext } from '../../../contexts/auth';
import Faq from '../../uiParts/Faq';
import AdminProductRegisterForm from '../../uiParts/AdminProductRegisterForm';
import CustomHead from '../CustomHead';
import { AddErrorList } from '../common/ProductRegister';
import { error } from '../../../validation/text';
import { sentryLog } from '../../../config/sentry';

const styles = {
  container: {
    '@media screen and (min-width:961px)': {
      ml: sideBarWidth
    },
    '@media screen and (max-width:600px)': {
      p: '24px 0'
    },
    '@media screen and (max-width:1440px)': {
      flexDirection: 'column'
    },
    '@media screen and (min-width:1441px)': {
      flexDirection: 'row'
    },
    '@media screen and (max-width:961px)': {
      mt: spHeaderHeight
    },
    p: '32px'
  },
  body: {
    '@media screen and (max-width:600px)': {
      minWidth: '340px',
      p: '0 16px'
    },
    '@media screen and (min-width:1441px)': {
      mr: '56px'
    }
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px'
    },
    fontSize: '32px',
    fontWeight: 500,
    color: gray100
  },
  titleContainer: {
    borderBottom: `1px solid ${gray70}`,
    pb: '24px',
    mb: '24px'
  },
  text: {
    fontSize: '16px',
    mb: '48px',
    color: gray100
  },
  button: {
    '@media screen and (max-width:1440px)': {
      display: 'none'
    },
    fontSize: '16px',
    p: '10px 16px',
    ...primaryButtonOutlinedColor
  },
  questionContainer: {
    '@media screen and (min-width:1441px)': {
      width: '520px',
      position: 'sticky',
      top: '32px'
    },
    backgroundColor: gray10,
    '@media screen and (max-width:1440px)': {
      mb: '125px'
    }
  },
  buttonContainer: {
    '@media screen and (min-width:1441px)': {
      display: 'none'
    },
    '@media screen and (min-width:961px)': {
      ml: sideBarWidth
    },
    backgroundColor: 'rgba(255, 255, 255, 0.80)',
    p: '12px 16px',
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 2,
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column'
  },
  spOutlinedButton: {
    fontSize: '16px',
    p: '10px 16px',
    ...primaryButtonOutlinedColor
  },
  spButton: {
    fontSize: '16px',
    fontWeight: 500,
    mb: '11px',
    p: '10px 16px',
    ...primaryButtonBackgroundColor
  }
};

interface response extends AxiosResponse {
  data: {
    message: string;
    errors: string;
  };
}

const AdminProductRegister: React.FC = () => {
  const { isLoggedIn } = useContext(AuthContext);
  const currentUserType = Number(localStorage.getItem('user_type'));
  const navigate = useNavigate();

  useEffect(() => {
    // 未ログイン時はログイン画面に飛ばす
    if (!isLoggedIn) return navigate('/auth/login');
    // 事務局画面で事務局ユーザーではない場合は、ホームに飛ばす
    if (currentUserType !== userType.admin) return navigate('/home');
  }, [isLoggedIn, currentUserType]);

  const {
    control,
    setValue,
    formState: { errors },
    handleSubmit,
    watch
  } = useForm<AdminProductRegisterType>({
    defaultValues: {
      categoryId: 0,
      materialId: 0,
      settingAt: null
    },
    resolver: yupResolver(adminProductSchema)
  });

  const [errorText, setErrorText] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      const kind = registerKind.create;
      await adminRegisterProduct(kind, watch);
      navigate('/operator/product/complete', { state: { status: 'register' } });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
        setOpen(true);
      } else {
        setErrorText(common);
        setOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmitForSaveDraft = async () => {
    try {
      setLoading(true);
      // 下書き保存
      await adminRegisterProduct(registerKind.draft, watch);
      navigate('/operator/product/complete', { state: { status: 'draft' } });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const errorResponse = axiosError.response as ErrorResponseType;
        setErrorText(errorResponse.data.message);
        setOpen(true);
      } else {
        setErrorText(common);
        setOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const [firstSubmit, setFirstSubmit] = useState<boolean>(false);

  // yupで設定できないエラーを管理
  const [addErrorList, setAddErrorList] = useState<AddErrorList | null>(null);

  // CO2原単位と削減率のどちらか必須
  const co2RequiredCheck = (): boolean => {
    const flg = !watch('emissionIntensity') && !watch('reduceRate');
    setAddErrorList((addErrorList) => ({
      ...addErrorList,
      co2RequiredError: {
        error: flg,
        errorMessage: flg ? error.requiredCo2 : null
      }
    }));
    return flg;
  };

  useEffect(() => {
    firstSubmit && co2RequiredCheck();
  }, [watch('emissionIntensity'), watch('reduceRate')]);

  // CO2算定根拠 種別が第三者認証の時に、名称を必須にする
  const caluclatedNameCheck = (): boolean => {
    const flg =
      Number(watch('calculationBasisType')) === 1 &&
      !watch('calculationBasisName');
    setAddErrorList((addErrorList) => ({
      ...addErrorList,
      co2BasisNameRequiredError: {
        error: flg,
        errorMessage: flg ? error.required : null
      }
    }));
    return flg;
  };

  useEffect(() => {
    firstSubmit && caluclatedNameCheck();
  }, [watch('calculationBasisType'), watch('calculationBasisName')]);

  // 下書きの場合、商品名必須
  const nameRequiredCheck = (): boolean => {
    const flg = !watch('name');
    setAddErrorList((addErrorList) => ({
      ...addErrorList,
      nameRequiredError: {
        error: flg,
        errorMessage: flg ? error.required : null
      }
    }));
    return flg;
  };

  useEffect(() => {
    firstSubmit && nameRequiredCheck();
  }, [watch('name')]);

  // 下書きの場合、会社名必須
  const companyRequiredCheck = (): boolean => {
    const flg = !watch('company');
    setAddErrorList((addErrorList) => ({
      ...addErrorList,
      companyRequiredError: {
        error: flg,
        errorMessage: flg ? error.required : null
      }
    }));
    return flg;
  };

  useEffect(() => {
    firstSubmit && companyRequiredCheck();
  }, [watch('company')]);

  const isButtonDisabled = () => {
    if (!firstSubmit) return false;
    return (
      Boolean(errors.alias) ||
      Boolean(errors.name) ||
      Boolean(errors.specification) ||
      Boolean(errors.summary) ||
      Boolean(errors.images) ||
      Boolean(errors.document) ||
      Boolean(errors.categoryId) ||
      Boolean(errors.materialId) ||
      Boolean(errors.calculationSpecification) ||
      Boolean(errors.emissionIntensity) ||
      Boolean(errors.reduceRate) ||
      Boolean(errors.calculationSupplement) ||
      Boolean(errors.calculationBasisType) ||
      Boolean(errors.calculationBasisName) ||
      Boolean(errors.calculationBasisFile) ||
      Boolean(errors.calculationBasisFileUrl) ||
      Boolean(errors.carbonOffsetFlg) ||
      Boolean(errors.otherCertification) ||
      Boolean(errors.referenceUnitPrice) ||
      Boolean(errors.settingAt) ||
      Boolean(errors.priceRemarks) ||
      Boolean(errors.relatedUrl) ||
      Boolean(errors.remark) ||
      Boolean(errors.assignee) ||
      Boolean(errors.recommendFlg) ||
      Boolean(errors.operatorName) ||
      Boolean(errors.operatorMemo) ||
      Boolean(addErrorList?.co2BasisNameRequiredError?.error) ||
      Boolean(addErrorList?.co2RequiredError?.error)
    );
  };

  return (
    <>
      <CustomHead title="事務局 建材登録 | CMCOS[コムコス]" />
      <Grid container>
        <CustomLoading loading={loading} />
        <SideBar />
        <Grid container sx={styles.container} justifyContent={'center'}>
          <Grid item sx={styles.body} xs>
            <Grid
              container
              sx={styles.titleContainer}
              justifyContent="space-between"
            >
              <Typography sx={styles.title}>事務局 建材登録</Typography>
              <Button
                variant="outlined"
                sx={styles.button}
                onClick={async () => {
                  setFirstSubmit(true);
                  const nameRequiredFlg = nameRequiredCheck();
                  const companyRequiredFlg = companyRequiredCheck();
                  !nameRequiredFlg &&
                    !companyRequiredFlg &&
                    (await onSubmitForSaveDraft());
                }}
              >
                下書きに保存する
              </Button>
            </Grid>
            <AdminProductRegisterForm
              control={control}
              setValue={setValue}
              errors={errors}
              handleSubmit={handleSubmit}
              onSubmit={onSubmit}
              isButtonDisabled={isButtonDisabled()}
              isNew={true}
              watch={watch}
              addErrorList={addErrorList}
              co2RequiredCheck={co2RequiredCheck}
              caluclatedNameCheck={caluclatedNameCheck}
              setFirstSubmit={setFirstSubmit}
            />
          </Grid>
          <Grid item>
            <Grid container sx={styles.questionContainer}>
              <Faq />
            </Grid>
          </Grid>
        </Grid>
        <CustomSnackbar
          autoHideDuration={6000}
          onClose={() => setOpen(false)}
          open={open}
          text={errorText}
        />
        <Grid sx={styles.buttonContainer}>
          <Button
            variant="contained"
            sx={styles.spButton}
            onClick={async () => {
              setFirstSubmit(true);
              const co2RequiredFlg = co2RequiredCheck();
              const caluclatedNameFlg = caluclatedNameCheck();
              await handleSubmit(async () => {
                !co2RequiredFlg && !caluclatedNameFlg && (await onSubmit());
              })();
            }}
            disabled={isButtonDisabled()}
          >
            建材を登録する
          </Button>
          <Button
            variant="outlined"
            sx={styles.spOutlinedButton}
            onClick={async () => {
              setFirstSubmit(true);
              const nameRequiredFlg = nameRequiredCheck();
              const companyRequiredFlg = companyRequiredCheck();
              !nameRequiredFlg &&
                !companyRequiredFlg &&
                (await onSubmitForSaveDraft());
            }}
          >
            下書きに保存する
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default AdminProductRegister;
